import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { Spinner } from './components/common/Spinner';
import { CommandPalette } from './components/common/CommandPalette';
import { useTheme } from './hooks/useTheme';
import { useCommandPaletteStore } from './store/commandPaletteStore';
import { usePreferencesStore } from './store/preferencesStore';

// Activates theme watching + Ctrl/Cmd+K shortcut
function ThemeAndShortcuts() {
  useTheme();
  const toggle = useCommandPaletteStore((s) => s.toggle);
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [toggle]);
  return null;
}

// Redirects to the user's preferred landing page (used by the index & catch-all routes)
function LandingRedirect() {
  const defaultLandingPage = usePreferencesStore((s) => s.defaultLandingPage);
  return <Navigate to={defaultLandingPage} replace />;
}

// ── Lazy-loaded pages (code-split per route) ──────────────────────────────────
const LoginPage       = lazy(() => import('./modules/auth/LoginPage'));
const RegisterPage    = lazy(() => import('./modules/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./modules/auth/VerifyEmailPage'));
const ForbiddenPage   = lazy(() => import('./modules/errors/ForbiddenPage'));
const DashboardPage   = lazy(() => import('./modules/dashboard/DashboardPage'));
const HumanizerPage      = lazy(() => import('./modules/ai-tools/humanizer/HumanizerPage'));
const DetectorTablePage    = lazy(() => import('./modules/ai-tools/detector/DetectorTablePage'));
const DetectorResultPage   = lazy(() => import('./modules/ai-tools/detector/DetectorResultPage'));
const JournalFinderPage  = lazy(() => import('./modules/ai-tools/journal-finder/JournalFinderPage'));
const JournalBrowsePage  = lazy(() => import('./modules/journals/JournalBrowsePage'));
const FileManagerPage    = lazy(() => import('./modules/files/FileManagerPage'));
const NotificationsPage          = lazy(() => import('./modules/notifications/NotificationsPage'));
const ProfileSettingsPage        = lazy(() => import('./modules/profile/ProfileSettingsPage'));
const AccountSettingsPage        = lazy(() => import('./modules/settings/AccountSettingsPage'));
const PricingPage                = lazy(() => import('./modules/pricing/PricingPage'));
const MySubscriptionsPage        = lazy(() => import('./modules/pricing/MySubscriptionsPage'));
const MockCheckoutPage           = lazy(() => import('./modules/payments/MockCheckoutPage'));
const HistoryPage                = lazy(() => import('./modules/ai-tools/history/HistoryPage'));
const RunDetailPage              = lazy(() => import('./modules/ai-tools/history/RunDetailPage'));

function PublicLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeAndShortcuts />
      <CommandPalette />
      <Routes>
        {/* ── Public (each has its own Suspense boundary) ──────────────────── */}
        <Route path="/login" element={
          <Suspense fallback={<PublicLoader />}><LoginPage /></Suspense>
        } />
        <Route path="/register" element={
          <Suspense fallback={<PublicLoader />}><RegisterPage /></Suspense>
        } />
        <Route path="/verify" element={
          <Suspense fallback={<PublicLoader />}><VerifyEmailPage /></Suspense>
        } />
        <Route path="/403" element={
          <Suspense fallback={<PublicLoader />}><ForbiddenPage /></Suspense>
        } />

        {/* ── Authenticated (AppLayout owns Suspense + transition internally) ── */}
        <Route element={<AppLayout />}>
          <Route index element={<LandingRedirect />} />

          {/* All authenticated roles */}
          <Route path="/dashboard"           element={<DashboardPage />} />

          {/* All authenticated users — AI tools require no role/plan gate client-side, only auth */}
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile"       element={<ProfileSettingsPage />} />
          <Route path="/settings"      element={<AccountSettingsPage />} />
          <Route path="/pricing"       element={<PricingPage />} />
          <Route path="/pricing/subscriptions" element={<MySubscriptionsPage />} />
          <Route path="/payments/mock-checkout/:intentId" element={<MockCheckoutPage />} />

          <Route path="/ai-tools/humanizer"      element={<HumanizerPage />} />
          <Route path="/ai-tools/detector"          element={<DetectorTablePage />} />
          <Route path="/ai-tools/journal-finder" element={<JournalFinderPage />} />
          <Route path="/ai-tools/history"        element={<HistoryPage />} />
          <Route path="/ai-tools/history/:id"    element={<RunDetailPage />} />
          <Route path="/journals/browse"         element={<JournalBrowsePage />} />
          <Route path="/files"                   element={<FileManagerPage />} />
        </Route>

        {/* ── AI Detector results — full-screen custom chrome, no AppLayout ── */}
        <Route element={<ProtectedRoute />}>
          <Route path="/ai-tools/detector/:runId" element={
            <Suspense fallback={<PublicLoader />}><DetectorResultPage /></Suspense>
          } />
        </Route>

        {/* ── Catch-all ───────────────────────────────────────────────────── */}
        <Route path="*" element={<LandingRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}
